import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PriceContext } from '../Context/PriceContext';
import calendar_icon from '../Assets/calendar_icon.png';
import pen_paper_icon from '../Assets/pen_paper_icon.png';
import './PriceCalculator.css';
import Mount from '../Mount';
import { Helmet } from 'react-helmet';
import './Drop.css';

// Move Drop component outside of Consultation to avoid context issues
const Drop = ({ className, options, onSelect, icon, paymentData, setPaymentData }) => {
  // Initialize selectedOption based on the context value
  const [selectedOption, setSelectedOption] = useState(
    className === 'DocumentType' 
      ? paymentData.documentType || ''
      : paymentData.deliveryTurnaround || ''
  );

  useEffect(() => {
    const defaultColor = '#d0dcdf'; 
    const dropdown = document.getElementById(className);
    if (dropdown) {
      const defaultOption = dropdown.querySelector('option');
      if (defaultOption) {
        defaultOption.style.color = defaultColor;
      }
    }
  }, [className]);

  // Update local state when context changes
   useEffect(() => {
    setPaymentData(prevData => ({
      ...prevData,
      services: {
        ...prevData.services,
        Consult: true
      },
      wordcount: '0' // Set wordcount to '0' here
    }));
  }, [setPaymentData]);

  const calculateDeadline = (option) => {
    const now = new Date();
    const utcDate = new Date(now.toUTCString());
    let daysToAdd;
    switch (option) {
      case "Same Day (4-6hrs)":
        return formatToEST(utcDate);
      case "Next Day (24hrs)":
        daysToAdd = 1;
        break;
      case "Priority (72 hours)":
        daysToAdd = 3;
        break;
      case "Standard (5-7 days)":
        daysToAdd = 7;
        break;
      default:
        daysToAdd = 0;
    }
    utcDate.setDate(utcDate.getDate() + daysToAdd);
    return formatToEST(utcDate);
  };

  const formatToEST = (date) => {
    const options = { timeZone: 'America/New_York', year: 'numeric', month: 'long', day: 'numeric' };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    return formatter.format(date);
  };

  const handleChange = (e) => {
    const option = e.target.value;
    setSelectedOption(option);
    const deliveryDate = calculateDeadline(option);
    
    // Update the context with the new selection
    setPaymentData(prevData => ({
      ...prevData,
      [className === 'DocumentType' ? 'documentType' : 'deliveryTurnaround']: option,
      deadline: deliveryDate
    }));
    
    onSelect(option);
  };

  return (
    <div className={`drop-container ${className}`}>
      <img src={icon} alt="icon" className="drop-icon" />

      <select 
        id={className} 
        onChange={handleChange}
        value={selectedOption} // This ensures the selection stays visible
      >
        {/* Placeholder option for specific dropdowns */}
        {className === 'DocumentType' && (
          <option value="">Select Reason for Consult</option>
        )}
        {className === 'DeliveryTurnaround' && (
          <option value="">Select Consultation Tier</option>
        )}

        {/* Render all options dynamically */}
        {options.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
};

const Consultation = () => {
  const { paymentData = {}, setPaymentData = () => {} } = useContext(PriceContext);
  const [deliveryTurnaround, setDeliveryTurnaround] = useState(paymentData.deliveryTurnaround || '');
  const [documentType, setDocumentType] = useState(paymentData.documentType || '');
  const navigate = useNavigate();

  // Set Consult service to true when component mounts
  useEffect(() => {
    setPaymentData(prevData => ({
      ...prevData,
      services: {
        ...prevData.services,
        Consult: true
      },
      
    }));
  }, [setPaymentData]);

  const handleDeliveryTurnaroundChange = (value) => {
    setDeliveryTurnaround(value);
  };

  const handleDocumentTypeChange = (value) => {
    setDocumentType(value);
  };

  const handleNewProjectClick = (e) => {
    e.preventDefault();
    
    if (deliveryTurnaround && documentType) {
      setPaymentData((prevData) => ({
        ...prevData,
        deliveryTurnaround,
        documentType,
        services: {
          ...prevData.services,
          Consult: true,
          
        }
      }));
      
      // Navigate to upload after setting data
      navigate('/upload');
    } else {
      alert("Please ensure all fields are correct before proceeding.");
    }
  };

  return (
    <div className='setter'>
      <Helmet>
        <title>Price Calculator|Contranorm Media & Production&nbsp;&trade;</title>
      </Helmet>
      <Mount>
        <div className="page">
          <h1>
            <div className="text">Schedule Consultation</div>
          </h1>
          <div className="underline"></div>
          <form onSubmit={handleNewProjectClick}>
            <div className="price-options">
              <div className="doubleColumn">
               
                <div className='dropOptions'>
                  <Drop
                    className='DocumentType'
                    options={[
                      "Affidavit",
                      "Business Letter",
                      "Cease & Desist",
                      "(EEOC) Equal Employment Opportunity Commission",
                      "Formal Grievance",
                      "Formal Notice",
                      "Government Resume",
                      "Grant Proposal",
                      "Informal Resolution Attempt",
                      "Initial Filing (Administrative Proceeding)",
                      "Letter of Inquiry",
                      "Memorandum",
                      "(MSPB) Merit System Protection Board",
                      "Modern Resume",
                      "Motion",
                      "Notice of Non-Compliance",
                      "(OSC) Office of Special Counsel",
                      "(OWCP) Office of Worker's Compensation",
                      "Pleading",
                      "Professional Resume",
                      "Statement of Fact",
                      "Other - Service Type"
                    ].sort()} // Sorting the options array alphabetically
                    onSelect={handleDocumentTypeChange}
                    icon={pen_paper_icon}
                    paymentData={paymentData}
                    setPaymentData={setPaymentData}
                  />
                  <Drop
                    className='DeliveryTurnaround' 
                    options={["Same Day (4-6hrs)", "Next Day (24hrs)", "Priority (72 hours)", "Standard (5-7 days)"]}
                    onSelect={handleDeliveryTurnaroundChange}
                    icon={calendar_icon}
                    paymentData={paymentData}
                    setPaymentData={setPaymentData}
                  />
                </div>
              </div>
            </div>
          </form>
            
            <div className="terms-link-text">
              <a href="https://compliance.contranormdigital.com/">Terms & Conditions</a>
               
              </div>
              <div className="submit-container">
            
                <button type="submit" className="submit" onClick={handleNewProjectClick}>
                  Consult
                </button>
                <button type="button" className="submit gray" onClick={() => navigate('/login-signup')}>Login</button>
            
              </div>
          </div>
      </Mount>
    </div>
  );
};

export default Consultation;